// Copyright ©️ 2026 eVolve MEP, LLC

import { LineMaterial, type LineMaterialParameters } from 'three/addons/lines/LineMaterial';

import {
  HighlightableGroup,
  type HighlightableObjectName,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/HighlightableGroup';
import { labelTheming } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/labelTheming';

export type HighlightableLineType = 'dimensionLine' | 'dottedLine';

export const highlightableLineStyles = {
  dimensionLine: {
    highlighted: {
      color: labelTheming.colors.primaryLight,
      linewidth: labelTheming.lineThickness.veryThick,
      dashed: false,
      depthTest: false,
    },
    unhighlighted: {
      color: labelTheming.colors.primary,
      linewidth: labelTheming.lineThickness.normal,
      dashed: false,
      depthTest: false,
    },
  },
  dottedLine: {
    highlighted: {
      color: labelTheming.colors.primaryLight,
      linewidth: labelTheming.lineThickness.thick,
      dashSize: 0.05,
      gapSize: 0.03,
      dashScale: 1,
      dashed: true,
      depthTest: false,
    },
    unhighlighted: {
      color: labelTheming.colors.primaryDark,
      linewidth: labelTheming.lineThickness.thin,
      dashSize: 0.05,
      gapSize: 0.03,
      dashScale: 1,
      dashed: true,
      depthTest: false,
    },
  },
} as const satisfies Record<HighlightableLineType, Record<'highlighted' | 'unhighlighted', LineMaterialParameters>>;

/**
 * A {@link HighlightableGroup} with a {@link LineMaterial} that can be highlighted and unhighlighted.
 */
export class HighlightableLineGroup extends HighlightableGroup {
  protected lineMaterial: LineMaterial;

  constructor(
    highlightableObjectName: HighlightableObjectName,
    public readonly lineType: HighlightableLineType,
  ) {
    super(highlightableObjectName);
    this.lineMaterial = new LineMaterial(highlightableLineStyles[lineType].unhighlighted);
  }

  highlight() {
    this.lineMaterial.color.set(highlightableLineStyles[this.lineType].highlighted.color);
    this.lineMaterial.linewidth = highlightableLineStyles[this.lineType].highlighted.linewidth;
    super.highlight();
  }

  unhighlight() {
    this.lineMaterial.color.set(highlightableLineStyles[this.lineType].unhighlighted.color);
    this.lineMaterial.linewidth = highlightableLineStyles[this.lineType].unhighlighted.linewidth;
    super.unhighlight();
  }

  dispose() {
    this.lineMaterial.dispose();
    super.dispose();
  }
}
